import React, { useEffect, useState, useRef } from "react";
import { notification, Button } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { authUserData } from "../utils/Helper";
import { patchRequest } from "../axios";
import ButtonField from "../common/htmlComponents/ButtonField";
import { t } from "i18next";

const SubscriptionNotification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasClosed, setHasClosed] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const hasCheckedOnMount = useRef(false);

  const canShowNotification = () => {
    const data = localStorage.getItem("subscriptionAlertShown");
    const today = new Date().toISOString().split("T")[0];

    try {
      const parsed = JSON.parse(data || "{}");
      if (parsed?.date === today) {
        return parsed.count < 2;
      }
      return true;
    } catch (error) {
      console.error("Error parsing subscriptionAlertShown:", error);
      return true;
    }
  };

  const updateNotificationCount = () => {
    const today = new Date().toISOString().split("T")[0];
    const data = localStorage.getItem("subscriptionAlertShown");
    let parsed = { date: today, count: 0 };

    try {
      if (data) {
        parsed = JSON.parse(data);
      }
    } catch (error) {
      console.error("Error parsing subscriptionAlertShown:", error);
    }

    if (parsed.date === today) {
      parsed.count += 1;
    } else {
      parsed = { date: today, count: 1 };
    }

    localStorage.setItem("subscriptionAlertShown", JSON.stringify(parsed));
  };

  const isAfterFivePM = () => {
    const now = new Date();
    const hours = now.getHours();
    return hours >= 17;
  };

  const updateSubscriptionData = (newData) => {
    const authData = JSON.parse(localStorage.getItem("auth_data") || "{}");
    authData.expiry_date = newData.expiry_date;
    authData.days_to_expire = newData.days_to_expire;
    localStorage.setItem("auth_data", JSON.stringify(authData));
  };

  const checkSubscriptionStatus = async () => {
    try {
      const userData = authUserData();
      const companyID = userData?.company;
      if (!companyID) {
        console.error("No company ID found in auth data");
        return null;
      }

      const response = await patchRequest(
        `company/${companyID}/company-details/`
      );
      const data = response.data;
      if (data.success) {
        updateSubscriptionData({
          expiry_date: data.data.expiry_date,
          days_to_expire: data.data.days_to_expire,
        });
        return { ...userData, ...data.data };
      } else {
        console.error("Failed to fetch subscription status:", data.message);
        return null;
      }
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return null;
    }
  };

  const showNotification = (userData, key = "subscription-expiring") => {
    if (isNotificationOpen || !canShowNotification()) return;

    const { expiry_date, active_subscription, days_to_expire } = userData;
    const expiry = dayjs(expiry_date);
    const today = dayjs();
    const daysLeft = expiry.diff(today, "day");

    if (daysLeft < 0) {
      setIsNotificationOpen(true);
      notification.error({
        message: "Subscription Expired",
        description:
          "Your subscription has expired. Please subscribe again to continue using the service",
        icon: <ExclamationCircleOutlined style={{ color: "#D32029" }} />,
        duration: 3,
        placement: "topRight",
        onClose: () => setIsNotificationOpen(false),
      });

      setTimeout(() => {
        // localStorage.removeItem('auth_token');
        // localStorage.removeItem('auth_data');
        // localStorage.removeItem('subscriptionAlertShown');
        navigate("/subscription-list");
      }, 800);
      return;
    }

    const shouldNotify = daysLeft <= 15;

    if (shouldNotify) {
      setIsNotificationOpen(true);
      const btn = (
        <Button
          type="primary"
          size="middle"
          onClick={() => {
            notification.close(key);
            navigate("/subscription-list");
            setIsNotificationOpen(false);
          }}
        >
          Renew Now
        </Button>
      );

      notification.open({
        key,
        message: (
          <div style={{ fontWeight: "bold", fontSize: 16, color: "#D32029" }}>
            🚨 Subscription Expiring
          </div>
        ),
        description: (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 14 }}>
              Your subscription will expire in {days_to_expire} day
              {days_to_expire !== 1 ? "s" : ""}.
              <br />
              Please renew your subscription to continue using all features.
            </div>
            <ButtonField
              type="primary"
              onClick={() => {
                notification.close(key);
                navigate("/subscription-list");
                setIsNotificationOpen(false);
              }}
              label={t("Renew Now")}
            />
          </div>
        ),
      });

      updateNotificationCount();
    }
  };

  useEffect(() => {
    const userData = authUserData();
    if (!userData?.expiry_date) return;

    const closedData = localStorage.getItem("subscriptionAlertClosed");
    let suppressNotification = false;

    try {
      const parsedClosed = JSON.parse(closedData || "{}");
      const today = new Date().toISOString().split("T")[0];
      if (
        parsedClosed?.date === today &&
        parsedClosed?.path !== location.pathname
      ) {
        suppressNotification = !isAfterFivePM();
      }
    } catch (error) {
      console.error("Error parsing subscriptionAlertClosed:", error);
    }

    const initializeSubscriptionCheck = async () => {
      if (
        location.pathname === "/subscription-list" &&
        !hasCheckedOnMount.current
      ) {
        hasCheckedOnMount.current = true;
        const updatedData = await checkSubscriptionStatus();
        if (updatedData) {
          showNotification(updatedData);
        } else {
          showNotification(userData);
        }
      } else if (
        !suppressNotification &&
        !hasClosed &&
        location.pathname !== "/subscription-list"
      ) {
        const updatedData = await checkSubscriptionStatus();
        if (updatedData) {
          showNotification(updatedData);
        } else {
          showNotification(userData);
        }
      }
    };

    initializeSubscriptionCheck();

    const subscriptionInterval = setInterval(async () => {
      if (!isNotificationOpen && canShowNotification()) {
        const updatedData = await checkSubscriptionStatus();
        if (updatedData) {
          showNotification(updatedData);
        }
      }
    }, 4 * 60 * 60 * 1000);

    const timeInterval = setInterval(() => {
      if (
        isAfterFivePM() &&
        canShowNotification() &&
        hasClosed &&
        !isNotificationOpen &&
        location.pathname !== "/subscription-list"
      ) {
        showNotification(userData, "subscription-expiring-after-5pm");
        setHasClosed(false);
      }
    }, 60000);

    if (location.pathname !== "/subscription-list") {
      hasCheckedOnMount.current = false;
    }

    return () => {
      clearInterval(subscriptionInterval);
      clearInterval(timeInterval);
    };
  }, [navigate, location.pathname, hasClosed, isNotificationOpen]);

  return null;
};

export default SubscriptionNotification;
